/**
 * Таблица планов: группы партий, партии и состав — виртуализированный список
 * TanStack. Строки, колонки, метки и состав — в `components/plans/*`
 * (нарезка 14.10.26); сюда импортёры приходят за `PlansTable` и
 * `PlanTotalsPatch`, как и раньше.
 */

import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { СлойУвеличения } from "@/components/PhotoZoom";
import { ApiFailure, apiFetch } from "@/lib/api";
import { type PlanGroup } from "@/lib/plans";
import type { PlanRow } from "@/lib/providers";
import { useWechatStates } from "@/lib/wechat";
import { GroupRow } from "./plans/GroupRow";
import { LinesBlock } from "./plans/LinesBlock";
import { ТолькоЧтение, pickState } from "./plans/PickBox";
import { PlanBatchPanel } from "./plans/PlanBatchPanel";
import { PlanLinePanel, type PanelSaved } from "./plans/PlanLinePanel";
import { PlanRowView } from "./plans/PlanRowView";
import { PICK, TAIL, W } from "./plans/style";
import type { BatchStatusSaved, LinesResponse, PlanLine, PlanTotalsPatch } from "./plans/types";
import { usePlanColumns } from "./plans/usePlanColumns";

export type { PlanTotalsPatch } from "./plans/types";

/** Плоский поток строк для виртуализатора: заголовки групп и партии в одном списке. */
type Flat =
  | { kind: "group"; group: PlanGroup }
  | { kind: "plan"; plan: PlanRow }
  | { kind: "lines"; plan: PlanRow };

export const PlansTable = memo(function PlansTable({
  rows,
  groups,
  loading,
  picked,
  onPick,
  foldAll,
  unfoldAll,
  wechatReload,
  windowDays,
  writeBlocked,
  onLineSaved,
  batchBlocked,
  onBatchSaved,
  readOnly = false,
  groupQr,
}: {
  rows: PlanRow[];
  /** Группы строит страница: ей же нужен порядок товаров для «открыть». */
  groups: PlanGroup[];
  loading: boolean;
  /** Отмеченные партии — по номеру партии. */
  picked: ReadonlySet<number>;
  onPick: (planIds: number[], on: boolean) => void;
  /** Счётчики нажатий кнопок подвала: вырос — выполняем команду. */
  foldAll?: number;
  unfoldAll?: number;
  /** Пачку в WeChat отправила страница: вырос — метки групп перечитываются. */
  wechatReload?: number;
  /** Окно уходимости из тулбара: 7 · 30 · 60 · 90 дней. Имя НЕ `window`:
   *  проп с таким именем затенил бы глобальный объект внутри компонента. */
  windowDays: number;
  /** Почему правка плана закрыта, словами; `null` — открыта. */
  writeBlocked: string | null;
  /** Размер записан: страница правит итоги партии в строке списка. */
  onLineSaved: (planId: number, totals: PlanTotalsPatch) => void;
  /** Почему закрыта смена статуса партии, словами; `null` — открыта. */
  batchBlocked: string | null;
  /** Статус партии записан: страница правит строку списка на месте. */
  onBatchSaved: (result: BatchStatusSaved) => void;
  /** Только смотреть — карточка поставщика: без галочек выбора (правку
   *  закрывает `writeBlocked`). Место галочки остаётся, чтобы колонки не съехали. */
  readOnly?: boolean;
  /** Папка для всей группы кликом по «без QR-папки» (разметка 28.09.26 12:59
   *  п.1): застава экрана, занятость записи и сама запись — у страницы. Нет —
   *  метка остаётся надписью. */
  groupQr?: { blocked: string | null; busy: boolean; create: (group: PlanGroup) => void };
}) {
  const [openPlanId, setOpenPlanId] = useState<number | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [lines, setLines] = useState<Record<number, LinesResponse>>({});
  // Открытые панели — номерами, а не копией строки: строка и партия берутся из
  // `lines`/`rows` на каждом рендере, и после записи панель видит новое.
  // Живут ЗДЕСЬ, а не в `LinesBlock`: виртуализатор снимает блок состава,
  // уехавший за край прокрутки, и панель закрылась бы вместе с ним.
  const [linePanel, setLinePanel] = useState<{ planId: number; lineId: number } | null>(null);
  const [batchPanel, setBatchPanel] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Отправка в WeChat — одним опросом на все папки экрана, а не по запросу на
  // группу: групп сотни, и каждая спрашивала бы сама.
  const qrIds = useMemo(
    () => [...new Set(groups.flatMap((group) => (group.qrId ? [group.qrId] : [])))],
    [groups],
  );
  const wechat = useWechatStates(readOnly ? [] : qrIds);
  const { reload: reloadWechat } = wechat;
  useEffect(() => {
    if (wechatReload) reloadWechat();
  }, [wechatReload, reloadWechat]);

  // Команды свёртки приходят СЧЁТЧИКОМ, а не флагом: флаг «всё свёрнуто» стал
  // бы вторым состоянием рядом с `openGroups`, и они разъехались бы на первом
  // же клике по треугольнику одной группы. Группы — через ссылку: они новые на
  // каждый refetch и правку плана, и в зависимостях схлопывали бы всё заново
  // после каждой записи размера (аудит 12.10.26, B42).
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  useEffect(() => {
    if (!foldAll) return;
    setOpenGroups(Object.fromEntries(groupsRef.current.map((group) => [group.key, false])));
  }, [foldAll]);

  useEffect(() => {
    if (!unfoldAll) return;
    // Пустая карта И ЕСТЬ «всё открыто»: группы развёрнуты по умолчанию, и
    // второй способ записать то же самое завёл бы расхождение.
    setOpenGroups({});
  }, [unfoldAll]);

  const { columns, увеличено } = usePlanColumns();

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Без этого `rowsById` индексируется ПОРЯДКОВЫМ номером строки, а не
    // нашим id: поиск по id находил чужую строку либо ничего, и партия
    // отрисовывалась пустой — с сохранением высоты, то есть провалом.
    getRowId: (row) => String(row.id),
  });

  const flat = useMemo(() => {
    const out: Flat[] = [];
    for (const group of groups) {
      out.push({ kind: "group", group });
      if (openGroups[group.key] === false) continue;
      for (const plan of group.plans) {
        out.push({ kind: "plan", plan });
        if (openPlanId === plan.id) out.push({ kind: "lines", plan });
      }
    }
    return out;
  }, [groups, openGroups, openPlanId]);

  // Виртуализация: в DOM живут только видимые строки. Без неё несколько тысяч
  // партий × десяток ячеек — это десятки тысяч узлов, и браузер спотыкается
  // уже на раскладке, а не на данных.
  const virtualizer = useVirtualizer({
    count: flat.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const item = flat[index];
      if (item?.kind === "group") return 34;
      if (item?.kind === "lines") {
        const loaded = lines[item.plan.id];
        // +22 — строка комментариев areserv над составом (`LinesBlock`).
        return (
          44 +
          (loaded?.lines.length ?? item.plan.line_count) * 30 +
          (loaded?.batch_comments.length ? 22 : 0)
        );
      }
      return 38;
    },
    // Ключ обязан быть стабильным, а не индексом. Раскрытие партии ВСТАВЛЯЕТ
    // блок состава в середину списка, и все последующие индексы съезжают —
    // измеренные высоты остаются на старых местах, и таблица идёт провалами.
    getItemKey: (index) => {
      const item = flat[index];
      if (!item) return index;
      if (item.kind === "group") return `g:${item.group.key}`;
      if (item.kind === "plan") return `p:${item.plan.id}`;
      return `l:${item.plan.id}`;
    },
    overscan: 12,
  });

  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer, lines, openPlanId, openGroups]);

  // Отказ CRM на состав — словами под партией с кнопкой «ещё раз», а не вечное
  // «Загружаем состав…» и unhandled rejection в консоли (аудит 12.10.26, B43).
  const [linesFailed, setLinesFailed] = useState<Record<number, string>>({});
  const loadLines = useCallback(async (plan: PlanRow) => {
    setLinesFailed((prev) => {
      const next = { ...prev };
      delete next[plan.id];
      return next;
    });
    try {
      const loaded = await apiFetch<LinesResponse>(`/api/plans/${plan.id}/lines`);
      setLines((prev) => ({ ...prev, [plan.id]: loaded }));
    } catch (error) {
      setLinesFailed((prev) => ({
        ...prev,
        [plan.id]: error instanceof ApiFailure ? error.message : "CRM не ответила",
      }));
    }
  }, []);

  const toggleLines = useCallback(
    async (plan: PlanRow) => {
      if (openPlanId === plan.id) {
        setOpenPlanId(null);
        return;
      }
      setOpenPlanId(plan.id);
      // Состав грузится по раскрытию, а не заранее: тянуть строки для всех
      // партий сразу — это восемь тысяч строк, которых никто не просил.
      if (!lines[plan.id]) await loadLines(plan);
    },
    [lines, openPlanId, loadLines],
  );

  // Одна строка состава — правкой на месте, без второго запроса.
  const patchLine = useCallback(
    (planId: number, lineId: number, patch: (line: PlanLine) => PlanLine) =>
      setLines((prev) => {
        const loaded = prev[planId];
        if (!loaded) return prev;
        return {
          ...prev,
          [planId]: {
            ...loaded,
            lines: loaded.lines.map((row) => (row.id === lineId ? patch(row) : row)),
          },
        };
      }),
    [],
  );

  const onPanelSaved = useCallback(
    (saved: PanelSaved) => {
      if (saved.kind === "comment") {
        patchLine(saved.plan_id, saved.line_id, (line) => ({
          ...line,
          comments: [saved.comment, ...line.comments],
        }));
        return;
      }
      const { result } = saved;
      patchLine(saved.plan_id, saved.line_id, (line) => ({
        ...line,
        status_id: result.status_id,
        status_name: result.status_name,
        comments: result.comment ? [result.comment, ...line.comments] : line.comments,
      }));
      onLineSaved(saved.plan_id, result.totals);
    },
    [patchLine, onLineSaved],
  );

  const panelPlan = linePanel ? rows.find((row) => row.id === linePanel.planId) : undefined;
  const panelLoaded = linePanel ? lines[linePanel.planId] : undefined;
  const panelLine = linePanel
    ? panelLoaded?.lines.find((line) => line.id === linePanel.lineId)
    : undefined;
  const batchPlan = batchPanel === null ? undefined : rows.find((row) => row.id === batchPanel);

  const headers = table.getHeaderGroups()[0]?.headers ?? [];
  const items = virtualizer.getVirtualItems();

  return (
    <ТолькоЧтение.Provider value={readOnly}>
    {/* `flex-1` на телефоне достаётся остаток высоты, а его нет: отбор выше
        съел всё. Доля экрана вместо остатка — виртуализации нужен контейнер
        ИЗВЕСТНОЙ высоты, и `70dvh` ей такой же честный, как остаток на
        десктопе. */}
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-[var(--radius-card)] border border-border bg-bg max-md:min-h-[70dvh]">
      {/* Шапка вынесена из области прокрутки: sticky внутри scroll-контейнера
          считается от НЕГО, а не от окна, и уезжает в середину данных. */}
      <div className="flex h-[34px] flex-none items-center border-b border-border bg-bg-subtle text-[11.5px] font-medium text-fg-muted">
        <div className={PICK} />
        {headers.map((header) => (
          <div
            key={header.id}
            className={"px-2.5 " + (W[header.column.id] ?? "w-[100px]")}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
          </div>
        ))}
        <div className={TAIL} />
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
        {loading && rows.length === 0 ? (
          <div className="p-6 text-[13px] text-fg-muted">Загружаем партии…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-[13px] text-fg-muted">
            Под этот отбор партий нет. Снимите часть условий — например, период.
          </div>
        ) : (
          <div
            className="relative w-full"
            style={{ height: virtualizer.getTotalSize() }}
          >
            {items.map((virtualRow) => {
              const item = flat[virtualRow.index];
              if (!item) return null;
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  className="absolute left-0 top-0 w-full"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  {item.kind === "group" ? (
                    <GroupRow
                      group={item.group}
                      wechat={item.group.qrId ? wechat.states[item.group.qrId] : undefined}
                      onWechat={wechat.put}
                      groupQr={groupQr}
                      open={openGroups[item.group.key] !== false}
                      pick={pickState(item.group, picked)}
                      onPick={(on) => onPick(item.group.plans.map((p) => p.id), on)}
                      onToggle={() =>
                        setOpenGroups((prev) => ({
                          ...prev,
                          [item.group.key]: prev[item.group.key] === false,
                        }))
                      }
                    />
                  ) : item.kind === "plan" ? (
                    <PlanRowView
                      row={item.plan}
                      open={openPlanId === item.plan.id}
                      picked={picked.has(item.plan.id)}
                      onPick={(on) => onPick([item.plan.id], on)}
                      table={table}
                      onToggle={() => void toggleLines(item.plan)}
                      onStatus={
                        // «Отгружен» ставит крон areserv — кнопки у такой партии нет.
                        readOnly || item.plan.status_id === 3
                          ? undefined
                          : () => setBatchPanel(item.plan.id)
                      }
                    />
                  ) : (
                    <LinesBlock
                      rows={lines[item.plan.id]?.lines}
                      batchComments={lines[item.plan.id]?.batch_comments ?? []}
                      onOpenLine={(line) => setLinePanel({ planId: item.plan.id, lineId: line.id })}
                      failure={linesFailed[item.plan.id]}
                      onRetry={() => void loadLines(item.plan)}
                      windowDays={windowDays}
                      writeBlocked={writeBlocked}
                      onSaved={(line, totals) => {
                        // Состав правится НА МЕСТЕ, без второго запроса: его
                        // ответ уже принёс и строку, и итоги партии.
                        patchLine(item.plan.id, line.id, () => line);
                        onLineSaved(item.plan.id, totals);
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <СлойУвеличения value={увеличено} />
    </div>
    {linePanel && panelLine ? (
      <PlanLinePanel
        lines={[
          {
            line_id: panelLine.id,
            plan_id: linePanel.planId,
            size: panelLine.size,
            qty: panelLine.qty_planned,
            ordered: panelLine.qty_ordered,
            status_id: panelLine.status_id,
            status_name: panelLine.status_name,
            comments: panelLine.comments,
            // Партии нет среди строк (отбор сменился) — сервер всё равно решит сам.
            batch_status_id: panelPlan?.status_id ?? 1,
          },
        ]}
        batchComments={panelLoaded?.batch_comments ?? []}
        writeBlocked={writeBlocked}
        onClose={() => setLinePanel(null)}
        onSaved={onPanelSaved}
      />
    ) : null}
    {batchPlan ? (
      <PlanBatchPanel
        plan={batchPlan}
        blocked={batchBlocked}
        onClose={() => setBatchPanel(null)}
        onSaved={onBatchSaved}
      />
    ) : null}
    </ТолькоЧтение.Provider>
  );
});
